/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useEffect, useState } from 'react';
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  useOutletContext
} from 'react-router-dom';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/userSlice';
// Actions
import {
  approveComment,
  unapproveComment,
  publishComment,
  unpublishComment
} from '../../main/comments/actions';
// BASE Components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
import BASELoading from '../../../@BASE/core/BASELoading';
import NavLinkAdapter from '../../../@BASE/core/NavLinkAdapter/NavLinkAdapter';
// Material UI Components
import { lighten } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator
} from '@mui/lab';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Timeline from '@mui/lab/Timeline';
// Custom Components
import moment from 'moment';
import authRoles from '../../auth/authRoles';
import CommentCardHeader from './CommentCardHeader';
import CommentContentCard from './CommentContentCard';
import IMIReplyContentCard from './IMIReplyContentCard';
import RepliesHeader from './RepliesHeader';

function IMIReplyItem(props) {
  const { reply, last, user, searchText, resource } = props;

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot className="my-8 flex h-24 w-24 items-center justify-center p-0 shadow-none">
          <Avatar
            sx={{
              backgroundColor: reply.creator.settings.color,
              color:
                reply.creator.settings.color != '#013BB1' &&
                reply.creator.settings.color != '#481FA7'
                  ? 'common.black'
                  : 'common.white',
              width: 24,
              height: 24
            }}
            className="text-12 font-bold"
          >
            {reply.creator.first_name[0]}
            {reply.creator.last_name[0]}
          </Avatar>
        </TimelineDot>

        {!last && <TimelineConnector />}
      </TimelineSeparator>

      <TimelineContent className="flex flex-col items-start p-0 pb-16">
        <Typography className="text-14 font-bold pl-16">
          {reply.creator.first_name} {reply.creator.last_name}
        </Typography>
        <Typography color="text.secondary" className="text-12 mb-16 pl-16">
          {reply.updated_date
            ? moment
                .utc(reply.updated_date)
                .local()
                .format('MMM DD, YYYY, h:mm A z')
            : 'No date available'}
        </Typography>
        <IMIReplyContentCard comment={reply} user={user} searchText={searchText} resource={resource} />
        {reply.replies.filter(
          (replyToThis) =>
            authRoles.editor.includes(user.role) ||
            (replyToThis?.review?.cycle !== 'prod_alpha_internal' &&
              replyToThis?.review?.cycle !== 'prod_beta_internal' &&
              replyToThis?.review?.cycle !== 'prod_final_internal')
        ).length > 0 && (
          <Box
            className="card-footer flex flex-col p-0 border-t-1 w-full"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? lighten(theme.palette.background.default, 0.4)
                  : lighten(theme.palette.background.default, 0.02)
            }}
          >
            <Timeline
              className="p-0 py-16"
              position="right"
              sx={{
                '& .MuiTimelineItem-root:before': {
                  display: 'none'
                }
              }}
            >
              {reply.replies
                .filter(
                  (replyToThis) =>
                    authRoles.editor.includes(user.role) ||
                    (replyToThis?.review?.cycle !== 'prod_alpha_internal' &&
                      replyToThis?.review?.cycle !== 'prod_beta_internal' &&
                      replyToThis?.review?.cycle !== 'prod_final_internal')
                )
                .map((replyToThis, index) => (
                  <IMIReplyItem
                    key={replyToThis.id}
                    reply={replyToThis}
                    last={reply.replies.length === index + 1}
                    user={user}
                    searchText={searchText}
                    resource={resource}
                  />
                ))}
            </Timeline>
          </Box>
        )}
      </TimelineContent>
    </TimelineItem>
  );
}

export default IMIReplyItem;
